/**
 * `window.tjPasskeyDebug("you@example.com")` — one-shot diagnosis of the passkey login path.
 *
 * A restricted ceremony can only answer "did it work"; when the browser jumps to the cross-device
 * QR there is no error at all, because from its point of view nothing matched. This asks the two
 * questions that separate the possible causes:
 *
 * 1. **What does the server think this account owns?** — the device envelopes, i.e. the credential
 *    ids the login will put in `allowCredentials`. A missing `device_envelopes` field also proves
 *    an older worker is still deployed.
 * 2. **What does this browser actually hold?** — a *discoverable* request (empty `allowCredentials`)
 *    lets the authenticator answer with any passkey it has for the rpId, without the account's
 *    records being involved. Comparing its id with (1) says whether the account is stale or the
 *    client is asking for the wrong thing.
 *
 * The probe does not sign anyone in: no token is stored, no envelope is opened, no PRF is derived.
 */

import {
  bytesToBase64,
  resolveEffectiveRpId,
  type WrappedDeviceMasterKey,
} from "@tensilelabs/auth-client";
import { loginDeviceEnvelopes, passkeyLogin } from "../api";
import { authDebug, describeClientCapabilities, describeEnvelope } from "./debug";

export interface PasskeyProbeResult {
  /** Device records the account has, as the login response reports them. */
  envelopes: Array<Pick<WrappedDeviceMasterKey, "device_id" | "credential_id_b64">>;
  /** True when the worker returned the multi-device field (i.e. the passkey fix is deployed). */
  workerReturnsDeviceEnvelopes: boolean;
  /** Credential id this browser answered a discoverable request with, if any. */
  localCredentialIdB64: string | null;
  /** The envelope that credential belongs to, if the account knows about it. */
  matchingDeviceId: string | null;
  /** Human-readable conclusion, also printed to the console. */
  verdict: string;
}

export async function diagnosePasskey(emailInput: string): Promise<PasskeyProbeResult> {
  const email = emailInput.trim().toLowerCase();
  const rpId = resolveEffectiveRpId("tensilelabs.com");

  authDebug("probe:start", { email, rpId });
  authDebug("probe:capabilities", await describeClientCapabilities());

  const login = await passkeyLogin({ email });
  const envelopes = loginDeviceEnvelopes(login);
  const workerReturnsDeviceEnvelopes = Array.isArray(login.device_envelopes);

  authDebug("probe:login-response", {
    workerReturnsDeviceEnvelopes,
    envelopeCount: envelopes.length,
    keys: Object.keys(login),
  });
  envelopes.forEach((envelope, index) => {
    authDebug(`probe:envelope[${index}]`, describeEnvelope(envelope));
  });

  let localCredentialIdB64: string | null = null;
  let matchingDeviceId: string | null = null;
  let verdict: string;

  if (!navigator.credentials?.get || typeof window.PublicKeyCredential === "undefined") {
    verdict =
      "This browser exposes no WebAuthn API, so the SDK falls back to a software passkey and no " +
      "prompt can appear. The login cannot work in this browser.";
    authDebug("probe:verdict", { verdict });
    return {
      envelopes: envelopes.map(({ device_id, credential_id_b64 }) => ({
        device_id,
        credential_id_b64,
      })),
      workerReturnsDeviceEnvelopes,
      localCredentialIdB64,
      matchingDeviceId,
      verdict,
    };
  }

  // Discoverable probe: no allowCredentials, no PRF. The authenticator answers with whatever
  // passkey it holds for this rpId, which is precisely what the restricted login cannot reveal.
  try {
    const assertion = (await navigator.credentials.get({
      publicKey: {
        rpId,
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [],
        userVerification: "discouraged",
        timeout: 60000,
      },
    })) as PublicKeyCredential | null;

    if (assertion) {
      localCredentialIdB64 = bytesToBase64(new Uint8Array(assertion.rawId));
      matchingDeviceId =
        envelopes.find((envelope) => envelope.credential_id_b64 === localCredentialIdB64)
          ?.device_id ?? null;
      authDebug("probe:discoverable-credential", {
        localCredentialIdB64,
        registeredToAccount: Boolean(matchingDeviceId),
        matchingDeviceId,
      });

      verdict = matchingDeviceId
        ? `This browser's passkey IS registered to the account (device ${matchingDeviceId}). The ` +
          "restricted login should therefore have prompted locally — send this whole log."
        : `This browser holds a passkey for rpId "${rpId}" that the account has NO device record ` +
          `for (credential ${localCredentialIdB64}). The account's device list is stale: signing ` +
          "in from this browser cannot unwrap the vault until this credential is registered as a " +
          "device.";
    } else {
      verdict = "The browser returned no credential for a discoverable request.";
    }
  } catch (cause) {
    const name = cause instanceof DOMException ? cause.name : "";
    authDebug("probe:discoverable-error", {
      name,
      message: cause instanceof Error ? cause.message : String(cause),
    });
    verdict =
      name === "NotAllowedError"
        ? "No passkey was selected (the prompt was dismissed, timed out, or this device has no " +
          `passkey for rpId "${rpId}").`
        : `The discoverable probe failed: ${cause instanceof Error ? cause.message : String(cause)}`;
  }

  authDebug("probe:verdict", { verdict });
  console.warn("[taskjinn-auth] verdict:", verdict);
  return {
    envelopes: envelopes.map(({ device_id, credential_id_b64 }) => ({
      device_id,
      credential_id_b64,
    })),
    workerReturnsDeviceEnvelopes,
    localCredentialIdB64,
    matchingDeviceId,
    verdict,
  };
}

/** Exposes the probe on `window` and records that it is available. */
export function installPasskeyDebug(): void {
  (window as unknown as { tjPasskeyDebug?: typeof diagnosePasskey }).tjPasskeyDebug =
    diagnosePasskey;
  authDebug("debug-helper-installed", {
    usage: "window.tjPasskeyDebug('you@example.com')",
    silence: 'localStorage["tensile:auth-debug"] = "0"',
  });
}
